import { useMutation } from '@apollo/client';
import type { ControlType } from '@risksmart-app/domain/src/types/consts';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildControlInput,
  UpdateControlMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateControlDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateControlTRPC } from './useUpdateControlTRPC';

type UpdateControlInput = Omit<UpdateChildControlInput, 'Type'> & {
  Type?: ControlType | null;
};

export const useUpdateControl = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateControlGraphQL, graphqlState] = useMutation(
    UpdateControlDocument,
    {
      update: (cache) => evictField(cache, 'control'),
      refetchQueries: [namedOperations.Query.getControlById],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateControlTRPC();

  const updateControl = async (
    variables: UpdateControlInput
  ): Promise<UpdateControlMutation> => {
    if (trpcEnabled) {
      const { schedule, ...rest } = variables;

      return trpcMutation.updateControl({
        ...rest,
        ...mapScheduleToTRPC(schedule),
      });
    }

    const result = await updateControlGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update control');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateControl,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControl,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
